import { YEAR_END_MAX_DATE, YEAR_END_MIN_DATE } from '@/shared/constants';
import { amlConnectedColumn } from '@/shared/grids/columns/amlConnectedColumn';
import { amlRiskLevelBadgeTemplate } from '@/shared/grids/columns/amlRiskLevelColumn';
import { booleanColumn } from '@/shared/grids/columns/booleanColumn';
import { charityNumberColumn } from '@/shared/grids/columns/charityNumberColumn';
import { customFieldColumns } from '@/shared/grids/columns/customFieldColumns';
import { dateColumn } from '@/shared/grids/columns/dateColumn';
import { hiddenColumn } from '@/shared/grids/columns/hiddenColumn';
import { idColumn } from '@/shared/grids/columns/idColumn';
import { notesExcerptColumn } from '@/shared/grids/columns/notesExcerptColumn';
import { notesIconColumn } from '@/shared/grids/columns/notesIconColumn';
import { organisationLinkColumn } from '@/shared/grids/columns/organisationLinkColumn';
import { pendingRequestCountColumn } from '@/shared/grids/columns/pendingRequestCountColumn';
import { registrationNumberColumn } from '@/shared/grids/columns/registrationNumberColumn';
import { relationshipTypeColumn } from '@/shared/grids/columns/relationshipTypeColumn';
import {
  completionDueTaskCountColumn,
  deadlineDateDueTaskCountColumn,
  startDueTaskCountColumn,
  uncompletedTaskCountColumn,
} from '@/shared/grids/columns/taskCountColumn';
import { textColumn } from '@/shared/grids/columns/textColumn';
import {
  AmlRiskLevel,
  BookkeepingFrequency,
  ClientType,
  MtdItQuarters,
  OrganisationType,
  PayrollFrequency,
  VatSchedule,
} from '@/shared/types/enum';
import { GridSettings } from '@/shared/types/grid';
import { enumOptions } from '@/shared/utils/enum';
import { importFailed } from '@/shared/utils/import';

import { OrganisationView } from '@prisma/client';

const registeredOfficeWarningTemplate =
  '${if(registeredOfficeWarning)}<i class="fa-solid fa-triangle-exclamation text-warning me-1"></i><span>${registeredOfficeWarning}</span>${else}<span class="text-muted">Not set</span>${/if}';

export const organisationGrid: GridSettings<OrganisationView> = {
  defaultSort: {
    name: 'asc',
  },
  extraSelect: ['companiesHouseDataLastStatus'],
  filterExpanders: {
    groupId: { expander: 'organisationGroupDescendants', field: 'id' },
  },
  columns: [
    // Hidden columns
    idColumn(),
    {
      ...booleanColumn({
        field: 'importFailed',
        header: 'Import Failed',
        visible: false,
      }),
      showInColumnChooser: false,
    },
    hiddenColumn({
      field: 'serviceIds',
    }),
    hiddenColumn({
      field: 'dueTimeFrames',
    }),
    hiddenColumn({
      field: 'assignedUserIds',
    }),
    hiddenColumn({
      field: 'accountsDueTimeFrames',
    }),
    hiddenColumn({
      field: 'amlCheckDueTimeFrames',
    }),
    hiddenColumn({
      field: 'bookkeepingDueTimeFrames',
    }),
    hiddenColumn({
      field: 'cisReturnDueTimeFrames',
    }),
    hiddenColumn({
      field: 'confirmationStatementDueTimeFrames',
    }),
    hiddenColumn({
      field: 'charityAnnualFilingDueTimeFrames',
    }),
    hiddenColumn({
      field: 'payrollDueTimeFrames',
    }),
    hiddenColumn({
      field: 'taxReturnDueTimeFrames',
    }),
    hiddenColumn({
      field: 'vatReturnDueTimeFrames',
    }),
    hiddenColumn({
      field: 'mtdItUpdateDueTimeFrames',
    }),
    hiddenColumn({
      field: 'groupId',
    }),
    // Visible columns
    notesIconColumn({
      field: 'hasNotes',
    }),
    relationshipTypeColumn({
      field: 'relationshipType',
      clientType: ClientType.Organisation,
    }),
    textColumn({
      header: 'Reference',
      field: 'internalReference',
      visible: false,
    }),
    textColumn({
      header: 'Name',
      field: 'name',
      width: 300,
      template: (organisation) => {
        return organisation.name
          ? organisation.name
          : importFailed(organisation)
            ? '<span class="text-danger">Invalid registration number</span>'
            : '<span class="text-more-muted">Importing...</span>';
      },
    }),
    textColumn({
      uid: 'companyType', // Keep as companyType for backwards compatiblity
      header: 'Type',
      field: 'organisationType',
      template: '${window.formatters.formatOrganisationType(organisationType)}',
      extra: {
        filter: {
          options: enumOptions(OrganisationType),
        },
      },
    }),
    textColumn({
      header: 'Company Status',
      field: 'companyStatus',
      template: '${window.formatters.formatCompanyStatus(companyStatus)}',
      visible: false,
    }),
    textColumn({
      header: 'Company Status Detail',
      field: 'companyStatusDetail',
      template: '${window.formatters.formatCompanyStatusDetail(companyStatusDetail)}',
      visible: false,
    }),
    registrationNumberColumn({
      field: 'registrationNumber',
    }),
    charityNumberColumn({
      field: 'charityNumber',
      visible: false,
    }),
    dateColumn({
      header: 'Date of Incorporation',
      field: 'incorporationDate',
      visible: false,
    }),
    dateColumn({
      header: 'Trading Start Date',
      field: 'tradingStartDate',
      visible: false,
    }),
    dateColumn({
      header: 'Year End',
      field: 'yearEndDate',
      format: 'dayLongMonth',
      extra: {
        filter: {
          minDate: YEAR_END_MIN_DATE,
          maxDate: YEAR_END_MAX_DATE,
        },
      },
    }),
    textColumn({
      header: 'UTR Number',
      field: 'utr',
      visible: false,
    }),
    booleanColumn({
      header: 'Auth Code Present',
      field: 'companiesHouseAuthenticationCode',
      visible: false,
    }),
    textColumn({
      header: 'Phone 1',
      field: 'mainTelephoneNumber',
      visible: false,
    }),
    textColumn({
      header: 'Phone 2',
      field: 'secondaryTelephoneNumber',
      visible: false,
    }),
    textColumn({
      header: 'Email 1',
      field: 'mainEmail',
      visible: false,
    }),
    textColumn({
      header: 'Email 2',
      field: 'secondaryEmail',
      visible: false,
    }),
    textColumn({
      header: 'Website',
      field: 'website',
      visible: false,
      template:
        '${if(website)}<a href="https://${website}" target="_blank" class="grid-link">${website}</a>${else}<span class="text-more-muted">Not set</span>${/if}',
    }),
    textColumn({
      header: 'Contact First Name',
      field: 'principalContactFirstName',
      visible: false,
    }),
    textColumn({
      header: 'Contact Last Name',
      field: 'principalContactLastName',
      visible: false,
    }),
    textColumn({
      header: 'Contact Phone',
      field: 'contactTelephoneNumber',
      visible: false,
    }),
    textColumn({
      header: 'Contact Email',
      field: 'principalContactEmail',
      visible: false,
    }),
    dateColumn({
      header: 'Accounts Last Made Up To',
      field: 'accountsLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Accounts Period End',
      field: 'accountsPeriodEndDate',
      visible: false,
    }),
    dateColumn({
      header: 'Accounts Due',
      field: 'accountsDueDate',
      due: true,
    }),
    dateColumn({
      header: 'Confirmation Statement Last Date',
      field: 'confirmationStatementLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Confirmation Statement Next Date',
      field: 'confirmationStatementNextMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Confirmation Statement Due',
      field: 'confirmationStatementDueDate',
      due: true,
    }),
    dateColumn({
      header: 'Charity Annual Filing Last Made Up To',
      field: 'charityAnnualFilingLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Charity Annual Filing Period End',
      field: 'charityAnnualFilingPeriodEndDate',
      visible: false,
    }),
    dateColumn({
      header: 'Charity Annual Filing Due',
      field: 'charityAnnualFilingDueDate',
      due: true,
    }),
    dateColumn({
      header: 'Registered as Tax Agent',
      field: 'registeredAsTaxAgentDate',
      visible: false,
    }),
    dateColumn({
      header: 'Tax Return Last Made Up To',
      field: 'taxReturnLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Tax Return Period End',
      field: 'taxPeriodEndDate',
      visible: false,
    }),
    dateColumn({
      header: 'Tax Return Due',
      field: 'taxReturnDueDate',
      due: true,
      visible: true,
    }),
    dateColumn({
      header: 'Tax Payment Last Made Up To',
      field: 'taxPaymentLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Tax Payment Due',
      field: 'taxPaymentDueDate',
      due: true,
      visible: false,
    }),
    textColumn({
      header: 'VAT Number',
      field: 'vatHmrcRegistrationNumber',
      visible: false,
    }),
    dateColumn({
      header: 'VAT Registered as Agent',
      field: 'registeredAsVatAgentDate',
      visible: false,
    }),
    dateColumn({
      header: 'VAT Registration Date',
      field: 'vatRegistrationDate',
      visible: false,
    }),
    dateColumn({
      header: 'VAT Deregistration Date',
      field: 'vatDeregistrationDate',
      visible: false,
    }),
    textColumn({
      header: 'VAT Quarters',
      field: 'vatQuarters',
      extra: {
        displayField: 'vatQuartersName',
        filter: {
          options: enumOptions(VatSchedule, '/'),
        },
      },
    }),
    dateColumn({
      header: 'VAT Last Made Up To',
      field: 'vatLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'VAT Period End',
      field: 'vatPeriodEndDate',
      visible: false,
    }),
    dateColumn({
      header: 'VAT Return Due',
      field: 'vatReturnDueDate',
      due: true,
    }),
    dateColumn({
      header: 'MTD IT Start Date',
      field: 'mtdItStartOn',
      visible: false,
    }),
    textColumn({
      header: 'MTD IT Quarters',
      field: 'mtdItQuarters',
      visible: false,
      template:
        '${if(mtdItStartOn)}${window.formatters.formatMtdItQuarters(mtdItQuarters)}${else}<span class="text-more-muted">Not set</span>${/if}',
      extra: {
        filter: {
          options: enumOptions(MtdItQuarters),
        },
      },
    }),
    dateColumn({
      header: 'MTD IT Last Made Up To',
      field: 'mtdItLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'MTD IT Period End',
      field: 'mtdItPeriodEndDate',
      visible: false,
    }),
    dateColumn({
      header: 'MTD IT Update Due',
      field: 'mtdItUpdateDueDate',
      due: true,
    }),
    dateColumn({
      header: 'MTD IT Final Period',
      field: 'mtdItExitOn',
      visible: false,
    }),
    textColumn({
      header: 'PAYE Reference',
      field: 'payeReference',
      visible: false,
    }),
    textColumn({
      header: 'PAYE Accounts Office Reference',
      field: 'payeAccountsOfficeReferenceNumber',
      visible: false,
    }),
    dateColumn({
      header: 'PAYE Registered as Agent',
      field: 'registeredAsPayeAgentDate',
      visible: false,
    }),
    textColumn({
      header: 'Payroll Frequency',
      field: 'payrollFrequency',
      visible: false,
      extra: {
        displayField: 'payrollFrequencyName',
        filter: {
          options: enumOptions(PayrollFrequency),
        },
      },
    }),
    dateColumn({
      header: 'Payroll Start',
      field: 'payrollStartDate',
      visible: false,
    }),
    dateColumn({
      header: 'Payroll Last Made Up To',
      field: 'payrollLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Payroll Due',
      field: 'payrollDueDate',
      due: true,
      visible: false,
    }),
    dateColumn({
      header: 'Payroll Payment Due',
      field: 'payrollPaymentDueDate',
      due: true,
      visible: false,
    }),
    dateColumn({
      header: 'CIS Registered as Agent',
      field: 'registeredAsCisAgentDate',
      visible: false,
    }),
    dateColumn({
      header: 'CIS Start',
      field: 'cisStartDate',
      visible: false,
    }),
    dateColumn({
      header: 'CIS Last Made Up To',
      field: 'cisLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'CIS Period End',
      field: 'cisPeriodEndDate',
      visible: false,
    }),
    dateColumn({
      header: 'CIS Return Due',
      field: 'cisReturnDueDate',
      due: true,
      visible: false,
    }),
    textColumn({
      header: 'Bookkeeping Frequency',
      field: 'bookkeepingFrequency',
      visible: false,
      extra: {
        displayField: 'bookkeepingFrequencyName',
        filter: {
          options: enumOptions(BookkeepingFrequency),
        },
      },
    }),
    dateColumn({
      header: 'Bookkeeping Start',
      field: 'bookkeepingStartDate',
      visible: false,
    }),
    dateColumn({
      header: 'Bookkeeping Last Made Up To',
      field: 'bookkeepingLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Bookkeeping Due',
      field: 'bookkeepingDueDate',
      due: true,
      visible: false,
    }),
    uncompletedTaskCountColumn({
      linkIdField: 'clientId',
    }),
    startDueTaskCountColumn({
      linkIdField: 'clientId',
      visible: false,
    }),
    completionDueTaskCountColumn({
      linkIdField: 'clientId',
      visible: false,
    }),
    deadlineDateDueTaskCountColumn({
      linkIdField: 'clientId',
      visible: false,
    }),
    pendingRequestCountColumn(),
    dateColumn({
      header: 'Added',
      field: 'createdTime',
      visible: false,
    }),
    dateColumn({
      header: 'Engagement Signed',
      field: 'engagementSignedDate',
      visible: false,
      emptyText: 'Not signed',
      emptyClass: 'text-danger',
    }),
    dateColumn({
      header: 'Last AML Risk Assessment',
      field: 'amlLastCheckedDate',
      visible: false,
      emptyText: 'Never',
      emptyClass: 'text-danger',
      headerTemplate: 'Last AML RA',
    }),
    dateColumn({
      header: 'AML Risk Assessment Due',
      field: 'amlCheckDueDate',
      visible: false,
      due: true,
      emptyText: 'Never',
      emptyClass: 'text-danger',
      headerTemplate: 'AML RA Due',
    }),
    textColumn({
      header: 'AML Risk Level',
      field: 'amlRiskLevel',
      visible: false,
      template: amlRiskLevelBadgeTemplate,
      extra: {
        filter: {
          options: enumOptions(AmlRiskLevel),
        },
      },
    }),
    textColumn({
      header: 'AML Notes',
      field: 'amlNotes',
      visible: false,
    }),
    amlConnectedColumn({
      field: 'xamaAccountId',
    }),
    organisationLinkColumn({
      header: 'Group',
      idField: 'groupCompanyId',
      nameField: 'groupCompanyName',
      width: 200,
      extra: {
        sortField: 'groupCompanyName',
        filter: {
          lookup: 'Organisation',
          filter: {
            groupMembers: { some: { archivedTime: null } },
          },
          includeArchived: true,
        },
      },
    }),
    textColumn({
      header: 'Partner',
      field: 'partnerId',
      extra: {
        displayField: 'partnerName',
        sortField: 'partnerName',
        filter: {
          lookup: 'MemberView',
        },
      },
    }),
    textColumn({
      header: 'Account Manager',
      field: 'accountManagerId',
      extra: {
        displayField: 'accountManagerName',
        sortField: 'accountManagerName',
        filter: {
          lookup: 'MemberView',
        },
      },
      width: 200,
    }),
    textColumn({
      header: 'Registered Office Warning',
      field: 'registeredOfficeWarning',
      visible: false,
      template: registeredOfficeWarningTemplate,
    }),
    notesExcerptColumn({
      field: 'notesExcerpt',
    }),
    dateColumn({
      header: 'Archived',
      field: 'archivedTime',
      visible: false,
    }),
    ...customFieldColumns(),
  ],
};
